import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../core/authContext';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { printStyles, ReportPages } from '../ProgressReport';
import { flowBlocks, printsNothing, REPORT_SECTIONS, type SectionId } from '../ProgressReport/flow';
import { fetchProgressReport, type ProgressReport } from '../ProgressReport/report';
import { REPORT } from '../ProgressReport/tokens';
import { usePrintRecorder } from '../ProgressReport/usage';
import { LIST_PATH, PrintButton } from './index';
import { LeftOutDialog, type LeftOutRow } from './LeftOutDialog';
import { classLine, DEFAULT_QUERY, gradeOf, printOrder, studentName, type ListQuery } from './listRows';
import { loadListState, loadPrintSections } from './listState';
import { handedSections, PrintSectionsDialog, sectionIds, usePrintSections } from './PrintSectionsDialog';
import { currentIdentity, onProgressReportsReset, progressReportSessionOrNone, type AuthSnapshot, type ProgressReportSession, type ProgressReportStudents } from './students';
import { TextButton } from './TextButton';
import { LIST } from './tokens';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

const CONCURRENCY = 3;

type StateStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
type Outcome = { kind: 'ready'; report: ProgressReport } | { kind: 'failed' };
type Batch = { auth: AuthSnapshot; selected: string[]; sort: ListQuery['sort']; list: ProgressReportStudents | null };

function countWord(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

function profileStudent(state: unknown): string | null {
  const s = state as { from?: unknown; studentId?: unknown } | null;
  return s?.from === 'profile' && typeof s.studentId === 'string' && s.studentId !== '' ? s.studentId : null;
}

function leftOutRow(id: string, list: ProgressReportStudents | null, report: ProgressReport): LeftOutRow {
  const student = list?.students.find((s) => s.studentId === id) ?? {
    studentId: id,
    firstName: report.student.firstName,
    lastName: report.student.lastName,
    school: report.student.school,
    grade: report.student.grade,
    teacher: report.student.teacher,
    teachers: [],
    class: report.student.class,
    latestBenchmark: null,
    inCurrentWindow: false,
  };
  return { id, student: studentName(student), teacher: student.teacher, class: classLine(student), grade: gradeOf(student) };
}

export function ProgressReportsPrintPage({
  load = fetchProgressReport,
  listSource = progressReportSessionOrNone,
  storage,
  sectionsStorage,
  print = () => window.print(),
  identity = currentIdentity,
}: {
  load?: (studentId: string, auth: AuthSnapshot) => Promise<ProgressReport>;
  listSource?: () => Promise<ProgressReportSession>;
  storage?: StateStorage | null;
  sectionsStorage?: StateStorage | null;
  print?: () => void;
  identity?: () => Promise<string>;
}) {
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const profileId = profileStudent(navState);
  const [handed] = useState(() => handedSections(navState));
  const { isDistrictAdmin, isSuperAdmin } = useAuth();
  const [batch, setBatch] = useState<Batch | null>(null);
  const [outcomes, setOutcomes] = useState<Map<string, Outcome>>(() => new Map());
  const [chosen, setChosen] = useState<Set<SectionId> | null>(null);
  const [pageCounts, setPageCounts] = useState<Map<string, number>>(() => new Map());
  const [seeWho, setSeeWho] = useState(false);
  const [fontsReady, setFontsReady] = useState(() => typeof document === 'undefined' || !document.fonts);
  const [generation, setGeneration] = useState(0);
  const sections = usePrintSections(identity, sectionsStorage);

  useEffect(
    () =>
      onProgressReportsReset(() => {
        setBatch(null);
        setOutcomes(new Map());
        setChosen(null);
        setGeneration((n) => n + 1);
      }),
    [],
  );

  useEffect(() => {
    const fonts = typeof document === 'undefined' ? undefined : document.fonts;
    if (!fonts) return;
    let live = true;
    void fonts.ready.then(() => {
      if (live) setFontsReady(true);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    listSource().then(
      (session) => {
        if (cancelled) return;
        const saved = (storage === undefined ? loadListState() : loadListState(storage)) ?? { query: DEFAULT_QUERY, selected: [] };
        setBatch({
          auth: session.auth,
          selected: profileId === null ? saved.selected : [profileId],
          sort: profileId === null ? saved.query.sort : 'asc',
          list: session.students,
        });
        setChosen(handed ?? (sectionsStorage === undefined ? loadPrintSections(session.auth.owner) : loadPrintSections(session.auth.owner, sectionsStorage)));
      },
      () => {
        if (!cancelled) setBatch(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [listSource, storage, sectionsStorage, profileId, handed, generation]);

  const selected = batch?.selected ?? [];
  const list = batch?.list ?? null;

  const order = useMemo(() => (batch === null ? null : printOrder(batch.selected, batch.list?.students ?? [], batch.sort)), [batch]);

  const auth = batch?.auth ?? null;
  const owner = auth?.owner ?? null;

  useEffect(() => {
    if (order === null || order.length === 0 || auth === null) return;
    let cancelled = false;
    let next = 0;
    const invalidate = () => {
      cancelled = true;
      setOutcomes(new Map());
      setBatch((prev) => (prev === null ? prev : { ...prev, selected: [] }));
    };
    const worker = async () => {
      while (!cancelled && next < order.length) {
        const id = order[next++]!;
        let outcome: Outcome;
        try {
          outcome = { kind: 'ready', report: await load(id, auth) };
        } catch {
          outcome = { kind: 'failed' };
        }
        if (cancelled) return;
        if ((await identity()) !== owner) {
          invalidate();
          return;
        }
        if (!cancelled) setOutcomes((prev) => new Map(prev).set(id, outcome));
      }
    };
    for (let i = 0; i < Math.min(CONCURRENCY, order.length); i++) void worker();
    return () => {
      cancelled = true;
    };
  }, [order, auth, owner, load, identity]);

  const total = order?.length ?? selected.length;
  const done = order !== null && chosen !== null && order.every((id) => outcomes.has(id));
  const sorted = useMemo(() => {
    const kept: { id: string; report: ProgressReport }[] = [];
    const leftOut: LeftOutRow[] = [];
    if (order === null || chosen === null) return { kept, leftOut };
    order.forEach((id) => {
      const outcome = outcomes.get(id);
      if (outcome?.kind !== 'ready') return;
      if (printsNothing(flowBlocks(outcome.report), chosen)) leftOut.push(leftOutRow(id, list, outcome.report));
      else kept.push({ id, report: outcome.report });
    });
    return { kept, leftOut };
  }, [order, outcomes, chosen, list]);
  const { kept, leftOut } = sorted;
  const skipped = order === null ? [] : order.filter((id) => outcomes.get(id)?.kind === 'failed');
  const pages = kept.reduce((sum, { id }) => sum + (pageCounts.get(id) ?? 0), 0);

  usePrintRecorder(profileId === null ? 'batch' : 'single', kept.length);

  const profileOutcome = profileId === null ? undefined : outcomes.get(profileId);
  const profileKey = profileOutcome?.kind === 'ready' ? profileOutcome.report.student.key || profileId : profileId;
  const nameOf = (id: string) => {
    const student = list?.students.find((s) => s.studentId === id);
    return student ? studentName(student) : id;
  };
  const sectionsLine = chosen === null ? '' : `${chosen.size} of ${REPORT_SECTIONS.length} sections`;
  const progress = done
    ? kept.length === 0
      ? `No reports to print · ${sectionsLine}`
      : `${countWord(kept.length, 'report', 'reports')} ready · ${countWord(pages, 'page', 'pages')} · ${sectionsLine}`
    : `Preparing ${outcomes.size} of ${countWord(total, 'report', 'reports')}…`;
  const changeChosen = (next: Set<SectionId>) => {
    sections.save(next);
    setChosen(next);
    const carried = typeof navState === 'object' && navState !== null ? (navState as Record<string, unknown>) : {};
    navigate('.', { replace: true, state: { ...carried, sections: sectionIds(next) } });
  };

  return (
    <Box className="report-shell" sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: REPORT.screenBg }}>
      {printStyles}
      <Box className="report-nav" sx={{ display: 'contents' }}>
        <Sidebar sections={SIDEBAR_SECTIONS} activeId="reports" />
      </Box>
      <Box component="main" sx={{ flex: 1, minWidth: 0, overflowX: 'auto', fontFamily: LIST.font }}>
        <Box
          className="report-toolbar"
          sx={{ bgcolor: '#FFFFFF', borderBottom: `1px solid ${LIST.divider}`, padding: '20px 24px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}
        >
          <Box>
            {profileId === null ? (
              <TextButton label="Back to student list" onClick={() => navigate(LIST_PATH)} startIcon={<ChevronLeftIcon sx={{ width: 20, height: 20 }} aria-hidden="true" />} />
            ) : (
              <TextButton label="Back to the student profile" onClick={() => navigate(`/roster/${encodeURIComponent(profileKey ?? profileId)}`)} startIcon={<ChevronLeftIcon sx={{ width: 20, height: 20 }} aria-hidden="true" />} />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
              <Typography component="h1" sx={{ fontFamily: LIST.font, fontSize: 28, lineHeight: '36px', fontWeight: 600, color: LIST.ink }}>
                {profileId === null ? 'Print student progress reports' : 'Print student progress report'}
              </Typography>
              <Typography data-testid="print-progress" sx={{ fontFamily: LIST.font, fontSize: 16, lineHeight: '24px', fontWeight: 500, color: LIST.muted }}>
                {batch === null ? 'Preparing…' : selected.length === 0 ? 'No students are selected for printing.' : progress}
              </Typography>
              {skipped.length > 0 && (
                <Typography data-testid="print-skipped" sx={{ fontFamily: LIST.font, fontSize: 16, lineHeight: '24px', fontWeight: 500, color: LIST.ink }}>
                  {`${countWord(skipped.length, 'report', 'reports')} could not be prepared: ${skipped.map(nameOf).join(', ')}.`}
                </Typography>
              )}
              {leftOut.length > 0 && (
                <Box
                  data-testid="print-left-out"
                  role="note"
                  sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: '12px', py: '8px', borderRadius: '8px', bgcolor: LIST.notice.bg, color: LIST.notice.fg, fontFamily: LIST.font, fontSize: 16, lineHeight: '24px', width: 'fit-content', maxWidth: '100%' }}
                >
                  <InfoOutlinedIcon sx={{ width: 20, height: 20, flexShrink: 0 }} aria-hidden="true" />
                  <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{`${countWord(leftOut.length, 'student', 'students')} left out: nothing on file for the chosen sections`}</Box>
                  <TextButton label="See who" onClick={() => setSeeWho(true)} />
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
              <Box
                component="button"
                type="button"
                disabled={chosen === null}
                onClick={sections.open}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 40,
                  px: '16px',
                  border: `1px solid ${LIST.primary}`,
                  borderRadius: '8px',
                  bgcolor: '#FFFFFF',
                  color: LIST.primary,
                  fontFamily: LIST.font,
                  fontSize: 16,
                  lineHeight: '20px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  '&:disabled': { borderColor: LIST.disabledBg, color: LIST.disabledFg, cursor: 'default' },
                }}
              >
                Change sections
              </Box>
              <PrintButton count={done && fontsReady ? kept.length : 0} label={profileId === null ? undefined : 'Print report'} onClick={print} />
            </Box>
          </Box>
        </Box>
        <Box className="report-stage" sx={{ padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
          {chosen !== null &&
            kept.map(({ id, report }) => (
              <ReportPages
                key={id}
                report={report}
                sections={chosen}
                onPageCount={(n) => setPageCounts((prev) => (prev.get(id) === n ? prev : new Map(prev).set(id, n)))}
              />
            ))}
        </Box>
      </Box>
      {sections.pending !== null && (
        <PrintSectionsDialog
          open
          title="Change sections"
          groupLabel={profileId === null ? 'Include in each report' : 'Include in the report'}
          primaryLabel="Update preview"
          chosen={chosen ?? sections.pending.chosen}
          onCancel={sections.cancel}
          onConfirm={changeChosen}
        />
      )}
      <LeftOutDialog open={seeWho} rows={leftOut} showTeacher={isDistrictAdmin || isSuperAdmin} onClose={() => setSeeWho(false)} />
    </Box>
  );
}
